import { GenericSuccessResponse } from 'legacy/common/types/response';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';

import {
  claimReservedTicket,
  getMyTicketForEvent,
  getMyTickets,
  getReservedTicket,
  requestFullRefund,
  requestPartialRefund,
  revokeTicket,
  getTicketToTransfer,
  transferTicket,
  getMyMultiTicketsForEvent,
  rollbackTicket,
  requestTicketRefund,
  refundEventTicket
} from './service';
import {
  GetMyTicketsResultDto,
  MultiTicketDto,
  ReservedTicketDto,
  TicketDto,
  UserTicketDto
} from './types';
import { ErrorResponse } from 'legacy/common/utils/error';

export function useGetMyTickets(options?: { limit: number }) {
  return useInfiniteQuery<GetMyTicketsResultDto, ErrorResponse>(
    ['tickets/me'],
    ({ pageParam }) => getMyTickets({ limit: options?.limit, pageParam })
  );
}

export function useGetMyTicketForEvent(eventId: string) {
  return useQuery<TicketDto, ErrorResponse>(['tickets/me', eventId], () =>
    getMyTicketForEvent(eventId)
  );
}

export function useGetMyMultiTicketsForEvent(eventId: string) {
  return useQuery<MultiTicketDto, ErrorResponse>(
    [`/events/${eventId}/all`, eventId],
    () => getMyMultiTicketsForEvent(eventId)
  );
}

export function useGetTicketToTransfer(ticketId: string) {
  return useQuery<UserTicketDto, ErrorResponse>(['ticket', ticketId], () =>
    getTicketToTransfer(ticketId)
  );
}

export function useRevokeTicketCommand() {
  return useMutation<
    { success: boolean },
    ErrorResponse,
    {
      eventId: string;
      userId: string;
    }
  >(revokeTicket);
}

export function useRollbackTicketCommand() {
  return useMutation<
    { success: boolean },
    ErrorResponse,
    {
      ticketId: string;
    }
  >(rollbackTicket);
}

export function useGetReservedTicket(shareToken: string) {
  return useQuery<ReservedTicketDto, ErrorResponse>(
    ['tickets/reserved/', shareToken],
    () =>
      getReservedTicket({
        shareToken
      }),
    {
      enabled: !!shareToken,
      retry: false
    }
  );
}

export function useClaimReservedTicketCommand() {
  return useMutation<
    TicketDto,
    ErrorResponse,
    { shareToken: string; isWaiverAccepted?: boolean }
  >(claimReservedTicket);
}

export function usetransferTicketCommand() {
  return useMutation<
    TicketDto,
    ErrorResponse,
    { ticketId: string; isWaiverAccepted?: boolean }
  >(transferTicket);
}

export function useRequestFullRefundCommand() {
  return useMutation<
    GenericSuccessResponse,
    ErrorResponse,
    {
      eventId: string;
      email: string;
      transactionIds: {
        purchaserTransactionId: string;
        subsequentTransactionIds: string[];
      };
    }
  >(requestFullRefund);
}

export function useRequestTicketRefundCommand() {
  return useMutation<
    GenericSuccessResponse,
    ErrorResponse,
    {
      ticketId: string;
    }
  >(requestTicketRefund);
}

export function useRefundEventTicketCommand() {
  return useMutation<
    GenericSuccessResponse,
    ErrorResponse,
    {
      ticketId: string;
      eventId: string;
    }
  >(refundEventTicket);
}

export function useRequestPartialRefundCommand() {
  return useMutation<
    void,
    ErrorResponse,
    {
      eventId: string;
      email: string;
      transactions: {
        transactionId: string;
        claimerIds: string[];
        numberOfUnclaimedTickets: number;
      }[];
    }
  >(requestPartialRefund);
}
